import { useAuthStore } from '@client/stores/AuthStore';
import { expireSession } from '@client/utils/auth/sessionExpiry';

import type { AuthSessionSnapshot } from './error-classification';
import { isUnauthorizedTrpcError, shouldExpireSessionOnUnauthorized } from './sessionHandling';

/**
 * Build an {@link AuthSessionSnapshot} from the live Zustand auth store — the one
 * adapter between the store singleton and the pure predicates in
 * `error-classification.ts`, which stay decoupled from it so they remain
 * unit-testable.
 *
 * Owned here rather than in `client.tsx` so the cache handlers and
 * `runImperativeMutation` classify session expiry from the same input: a field
 * added to the snapshot then reaches every caller at once.
 */
export function getAuthSessionSnapshot(): AuthSessionSnapshot {
    const authStore = useAuthStore.getState();
    return {
        authTokenExpired: authStore.authTokenExpired,
        isTokenExpired: () => authStore.isTokenExpired(),
        isLoggingOut: authStore.isLoggingOut,
    };
}

/**
 * The app-wide reaction to a server-returned denial, owned in one place so every
 * caller applies the same auth rules.
 *
 * `MutationCache.onError` / `QueryCache.onError` run it for hook-driven traffic;
 * `runImperativeMutation` runs it for calls that outlive their React owner and
 * therefore never reach a cache handler (see `imperative-mutation.ts`). Keeping
 * it here rather than inline in `client.tsx` avoids each new caller re-deriving
 * "is this UNAUTHORIZED worth expiring the session for" for itself.
 */
export const handleGlobalUnauthorizedError = (error: unknown) => {
    if (isUnauthorizedTrpcError(error) && shouldExpireSessionOnUnauthorized(window.location.pathname)) {
        expireSession('[tRPC] Global UNAUTHORIZED error detected, triggering auth redirect');
        return;
    }

    // Route stable server-side denial codes to dedicated screens. authProcedure throws FORBIDDEN
    // with these messages when the org or user state changes mid-session (e.g. role removed,
    // payment lapsed).
    if (typeof error === 'object' && error !== null && 'data' in error) {
        const trpcError = error as { data?: { code?: string }; message?: string };
        if (trpcError.data?.code === 'FORBIDDEN') {
            if (trpcError.message === 'payment_required_for_login') {
                if (window.location.pathname !== '/payment-pending') {
                    window.location.href = '/payment-pending';
                }
            }
            // no_role_assigned and other server denials surface via the existing error UI;
            // a dedicated redirect would orphan the user mid-flow. Leave for the per-screen handler.
        }
    }
};
